import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageTitle,
  ROOM_SEX_TEXT,
  TEXT_LINK,
  TextAreaField,
  TextField,
} from '@rooming/ui';
import { formatInZone } from '@rooming/web-core';
import { useId, useState } from 'react';
import { backoffice, type HotelTerms, type PlacementRequestItem } from '../api';
import { Loaded, ReasonDialog, useCommand } from '../common';

// Owner's requests of 2026-10-20.
//  · "Solicitudes de plaza": a passenger whose sex is "sin definir" asked for a bed in a women's or men's room. Only an
//    administrator decides — after contacting the parents — and writes with whom it was agreed.
//  · The hotel's terms and conditions: the hotel user loads a link and/or a text; passengers must accept them before
//    choosing a bed at that hotel. A hotel that loads nothing shows no checkbox.

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const PENDING_KEY = ['placement-requests', 'pending'] as const;
const RESOLVED_KEY = ['placement-requests', 'resolved'] as const;

const usePending = () =>
  useQuery({
    queryKey: PENDING_KEY,
    queryFn: async () => (await backoffice.placementRequests('pending')).items,
    refetchInterval: 60_000,
  });

/** The number of waiting requests, next to «Solicitudes de plaza» in the menu. */
export function PlacementBadge() {
  const pending = usePending();
  const count = pending.data?.length ?? 0;
  if (count === 0) return null;
  return (
    <span className="ml-2 inline-flex">
      <Badge tone="warning">
        <span aria-hidden="true">{count}</span>
        <span className="sr-only">{`${String(count)} pendientes`}</span>
      </Badge>
    </span>
  );
}

type Decision = { readonly kind: 'approve' | 'reject'; readonly item: PlacementRequestItem };

export function PlacementRequestsPage() {
  const pending = usePending();
  const resolved = useQuery({
    queryKey: RESOLVED_KEY,
    queryFn: async () => (await backoffice.placementRequests('resolved')).items,
  });
  const { busy, run } = useCommand();
  const [decision, setDecision] = useState<Decision>();

  async function decide(note: string): Promise<void> {
    if (decision === undefined) return;
    const { kind, item } = decision;
    const ok = await run(
      () =>
        kind === 'approve' ? backoffice.approvePlacement(item.id, note) : backoffice.rejectPlacement(item.id, note),
      kind === 'approve' ? 'Aprobada: el pasajero ya tiene su plaza.' : 'Solicitud rechazada.',
      [PENDING_KEY, RESOLVED_KEY, ['notifications']],
    );
    if (ok) setDecision(undefined);
  }

  return (
    <>
      <PageTitle subtitle="Pasajeros con sexo «sin definir» que pidieron una plaza en una habitación de mujeres o de varones.">
        Solicitudes de plaza
      </PageTitle>
      <Alert tone="info" title="Cómo se resuelve">
        Primero comunicate con los padres o el firmante. Si lo autorizan, aprobá la solicitud y anotá con quién lo
        acordaste: el pasajero queda en esa plaza. Si no, rechazala: no cambia nada y se le avisa al pasajero.
      </Alert>
      <h2 className="mt-6 mb-3 text-xl font-semibold">Pendientes</h2>
      <Loaded query={pending}>
        {(items) =>
          items.length === 0 ? (
            <EmptyState title="No hay solicitudes pendientes">
              Cuando un pasajero con sexo sin definir pida una plaza, aparece acá y en Notificaciones.
            </EmptyState>
          ) : (
            <ul className="flex flex-col gap-3">
              {items.map((item) => (
                <li key={item.id}>
                  <Card className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold break-words">
                        {item.member.firstNames} {item.member.lastNames}
                      </h3>
                      {item.slotTaken && <Badge tone="danger">La plaza ya está ocupada</Badge>}
                    </div>
                    <p className="text-base break-words">
                      Grupo {item.stay.groupCode} · {item.stay.groupName} · {item.stay.hotelName}
                    </p>
                    <p className="text-base">
                      Pidió la plaza {item.room.position} de <strong>{item.room.label}</strong> (habitación de{' '}
                      {ROOM_SEX_TEXT[item.room.sexRestriction]}) el {formatInZone(item.createdAt, 'UTC')} UTC.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        disabled={busy || item.slotTaken}
                        onClick={() => {
                          setDecision({ kind: 'approve', item });
                        }}
                      >
                        Aprobar
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={busy}
                        onClick={() => {
                          setDecision({ kind: 'reject', item });
                        }}
                      >
                        Rechazar
                      </Button>
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          )
        }
      </Loaded>

      <h2 className="mt-8 mb-3 text-xl font-semibold">Resueltas en los últimos 30 días</h2>
      <Loaded query={resolved}>
        {(items) =>
          items.length === 0 ? (
            <p className="text-base text-muted">Todavía no hay solicitudes resueltas.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {items.map((item) => (
                <li key={item.id} className="rounded-xl border border-line px-4 py-3 text-base break-words">
                  <strong>
                    {item.member.firstNames} {item.member.lastNames}
                  </strong>{' '}
                  · {item.room.label} ·{' '}
                  <Badge tone={item.status === 'APPROVED' ? 'success' : 'neutral'}>{STATUS_TEXT[item.status]}</Badge>
                  {item.note !== null && <span className="block text-sm text-muted">{item.note}</span>}
                </li>
              ))}
            </ul>
          )
        }
      </Loaded>

      <ReasonDialog
        open={decision !== undefined}
        title={decision?.kind === 'approve' ? 'Aprobar la solicitud' : 'Rechazar la solicitud'}
        confirmLabel={decision?.kind === 'approve' ? 'Aprobar' : 'Rechazar'}
        danger={decision?.kind === 'reject'}
        busy={busy}
        reasonLabel={decision?.kind === 'approve' ? 'Con quién lo acordaste' : 'Motivo'}
        reasonHint="Obligatorio. Anotá con quién hablaste (padre, madre o colegio) y qué acordaron. Queda en la auditoría; no se le muestra al pasajero."
        onConfirm={(note) => void decide(note)}
        onClose={() => {
          setDecision(undefined);
        }}
      >
        {decision?.kind === 'approve' ? (
          <p>
            {decision.item.member.firstNames} {decision.item.member.lastNames} queda en la plaza{' '}
            {decision.item.room.position} de {decision.item.room.label}. Hacelo solo después de hablar con la familia.
          </p>
        ) : (
          <p>
            No cambia nada: {decision?.item.member.firstNames} sigue como estaba y se le avisa que Funtour se comunicará
            con su familia.
          </p>
        )}
      </ReasonDialog>
    </>
  );
}

const STATUS_TEXT: Record<PlacementRequestItem['status'], string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CANCELLED: 'Retirada',
  SUPERSEDED: 'Sin efecto',
};

/**
 * The hotel user loads (or removes) the hotel's terms and conditions: an https link and/or a text. With nothing loaded
 * passengers see no checkbox; with something loaded they must accept it before choosing a bed. Saving changed terms asks
 * every passenger again.
 */
export function HotelTermsEditor({
  stayId,
  initial,
}: {
  readonly stayId: string;
  readonly initial: HotelTerms | null;
}) {
  const queryClient = useQueryClient();
  const { busy, run } = useCommand();
  const [url, setUrl] = useState(initial?.url ?? '');
  const [text, setText] = useState(initial?.text ?? '');
  const file = initial?.file ?? null;
  const loaded = initial !== null && (initial.url !== null || initial.text !== null || file !== null);
  const fileId = useId();
  const [fileError, setFileError] = useState<string>();
  const urlOk = url.trim() === '' || /^https:\/\/\S+$/.test(url.trim());

  async function save(next: { url: string | null; text: string | null }, done: string): Promise<void> {
    const ok = await run(() => backoffice.setHotelTerms(stayId, next), done, [['external-stays']]);
    if (ok) await queryClient.invalidateQueries({ queryKey: ['external-stays'] });
  }

  async function upload(chosen: File | undefined): Promise<void> {
    if (chosen === undefined) return;
    if (chosen.size > MAX_FILE_BYTES) {
      setFileError('El archivo pesa más de 5 MB. Comprimilo o exportalo de nuevo.');
      return;
    }
    setFileError(undefined);
    const ok = await run(() => backoffice.setHotelTermsFile(stayId, chosen), 'Archivo cargado.', [['external-stays']]);
    if (ok) await queryClient.invalidateQueries({ queryKey: ['external-stays'] });
  }

  async function removeAll(): Promise<void> {
    setUrl('');
    setText('');
    const ok = await run(
      async () => {
        await backoffice.setHotelTerms(stayId, { url: null, text: null });
        if (file !== null) await backoffice.removeHotelTermsFile(stayId);
      },
      'Quitaste los términos: ya no se le piden a los pasajeros.',
      [['external-stays']],
    );
    if (ok) await queryClient.invalidateQueries({ queryKey: ['external-stays'] });
  }

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">Términos y condiciones del hotel</h2>
        <Badge tone={loaded ? 'success' : 'neutral'}>{loaded ? 'Cargados' : 'No cargados'}</Badge>
      </div>
      <p className="text-base">
        Si cargás un archivo, un enlace o un texto, cada pasajero tiene que aceptarlos antes de elegir su habitación en
        tu hotel. Si no cargás nada, no se le pide nada. Cada vez que los cambiás se le pide de nuevo.
      </p>
      <div className="flex flex-col gap-2">
        <label htmlFor={fileId} className="text-sm font-medium text-ink">
          Archivo con los términos (PDF, imagen o Word, hasta 5 MB)
        </label>
        <input
          id={fileId}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,application/pdf,image/png,image/jpeg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          disabled={busy}
          className="min-h-12 w-full rounded-xl border border-line bg-surface px-3 py-2 text-base text-ink"
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            event.target.value = '';
            void upload(chosen);
          }}
        />
        {fileError !== undefined && <p className="text-sm text-danger">{fileError}</p>}
        {file !== null && (
          <p className="text-base break-words">
            Archivo actual: <strong>{file.name}</strong> ({Math.max(1, Math.round(file.size / 1024))} KB) ·{' '}
            <a
              href={`/api/v1/stays/${stayId}/hotel-terms/file`}
              target="_blank"
              rel="noopener noreferrer"
              className={TEXT_LINK}
            >
              Ver el archivo
            </a>{' '}
            ·{' '}
            <button
              type="button"
              className={TEXT_LINK}
              disabled={busy}
              onClick={() => {
                void run(() => backoffice.removeHotelTermsFile(stayId), 'Archivo quitado.', [['external-stays']]);
              }}
            >
              Quitar el archivo
            </button>
          </p>
        )}
      </div>
      <TextField
        label="Enlace a los términos (opcional)"
        type="url"
        inputMode="url"
        autoComplete="off"
        maxLength={500}
        placeholder="https://"
        hint="Tiene que empezar con https://"
        error={urlOk ? undefined : 'Usá una dirección que empiece con https://'}
        value={url}
        onChange={(event) => {
          setUrl(event.target.value);
        }}
      />
      <TextAreaField
        label="Texto de los términos (opcional)"
        rows={6}
        maxLength={20000}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
        }}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          loading={busy}
          disabled={!urlOk}
          onClick={() => {
            void save(
              { url: url.trim() === '' ? null : url.trim(), text: text.trim() === '' ? null : text },
              'Términos guardados.',
            );
          }}
        >
          Guardar términos
        </Button>
        {loaded && (
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              void removeAll();
            }}
          >
            Quitar términos
          </Button>
        )}
      </div>
    </Card>
  );
}
